import { useEffect, useState } from "react";
import type { MaterialExtensionConfig } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { CompactCheck, CompactColor, CompactSlider, Row } from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";

const CC_TINT_IOR_INERT: InertTag = {
  reason:
    "Stored, never read. MetalExtensions pushes uCcTint / uCcTintColor / uCcThickness to the shader and nothing else \u2014 the tint IOR never reaches a uniform.",
};
const SS_BEVEL_INERT: InertTag = {
  reason:
    "Stored, never read. There is no per-material bevel pass; the working control is the global Post Processing > SSBevel section, which FramePipeline does consume.",
};

/**
 * The Sparkle Bump sliders' ranges. Exported because Basic mode's metal Sparkle
 * control writes the same three fields (basic/targets.ts `metalExt`), and its
 * guard (scripts/check-basic-macros.ts) proves Basic's bands sit inside THESE
 * ranges and on THESE steps — the numbers the Advanced sliders actually use.
 */
export const SPARKLE_SLIDERS = {
  sparkleBumpScale: { min: 0, max: 0.001, step: 0.00001 },
  sparkleFlakeScale: { min: 100, max: 10000, step: 10 },
  sparkleFlakeRadius: { min: 0.01, max: 1, step: 0.01 },
} as const;

const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Material extension write failed.";

/** Per-material shading extensions (v21, iJewel EXTENSION rows). Native ones
 * wire through; FragmentClipping uses a real clip plane; the rest round-trip. */
export function MaterialExtensionsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const groupId = useDomainState("selection") as string | null;
  const [c, setC] = useState<MaterialExtensionConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    if (!groupId) { setC(null); return; }
    let alive = true;
    r.getGroupMaterialExtensions(groupId).then(
      (v) => { if (alive) setC(v); },
      // A failed read used to render as "Gem materials use the Diamond
      // section." — the wrong explanation for a dead RPC, with no way to tell
      // the two apart.
      (e: unknown) => { if (alive) { setC(null); setError(errText(e)); } },
    );
    return () => { alive = false; };
  }, [groupId, r]);
  if (!groupId) return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Select a metal/standard part.</span>;
  if (!c) {
    return error
      ? <span className="block px-3 py-1.5 text-[11px] text-ed-red">{error}</span>
      : <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Gem materials use the Diamond section.</span>;
  }
  /** Every row `void`s this, so a rejection would be an unhandled promise and
   * the checkbox would stay on its old value with nothing said. Report inline;
   * a null result means the viewer refused the patch for this material. */
  const set = (patch: Partial<MaterialExtensionConfig>) => {
    setError(null);
    void r.setGroupMaterialExtensions(groupId, patch).then(
      (p) => { if (p) setC(p); else setError("This material does not accept that extension."); },
      (e: unknown) => setError(errText(e)),
    );
  };
  const head = (t: string) => <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">{t}</div>;
  return (
    <div className="flex flex-col">
      {head("Anisotropy")}
      <Row label="Enabled"><CompactCheck checked={c.anisotropyEnabled} onChange={(v) => void set({ anisotropyEnabled: v })} /></Row>
      <Row label="Factor"><CompactSlider value={c.anisotropyFactor} min={-2} max={2} step={0.01} onChange={(v) => void set({ anisotropyFactor: v })} /></Row>
      {head("Triplanar")}
      <Row label="Enabled"><CompactCheck checked={c.triplanarEnabled} onChange={(v) => void set({ triplanarEnabled: v })} /></Row>
      <Row label="Scale"><CompactSlider value={c.triplanarScale} min={0.1} max={10} step={0.1} onChange={(v) => void set({ triplanarScale: v })} /></Row>
      <Row label="Blend"><CompactSlider value={c.triplanarBlend} min={1} max={10} step={0.1} onChange={(v) => void set({ triplanarBlend: v })} /></Row>
      {head("Thin Film")}
      <Row label="Enabled"><CompactCheck checked={c.thinFilmEnabled} onChange={(v) => void set({ thinFilmEnabled: v })} /></Row>
      <Row label="Intensity"><CompactSlider value={c.thinFilmIntensity} min={0} max={1} step={0.01} onChange={(v) => void set({ thinFilmIntensity: v })} /></Row>
      {head("Fragment Clipping")}
      <Row label="Enabled"><CompactCheck checked={c.clippingEnabled} onChange={(v) => void set({ clippingEnabled: v })} /></Row>
      <Row label="Clip Height (Y)"><CompactSlider value={c.clipPosition.y} min={-2} max={2} step={0.01} onChange={(v) => void set({ clipPosition: { ...c.clipPosition, y: v } })} /></Row>
      <Row label="Invert"><CompactCheck checked={c.clipInvert} onChange={(v) => void set({ clipInvert: v })} /></Row>
      {head("Flat Material")}
      <Row label="Make 2D"><CompactCheck checked={c.flat2D} onChange={(v) => void set({ flat2D: v })} /></Row>
      {head("Sparkle Bump")}
      <Row label="Enabled"><CompactCheck checked={c.sparkleEnabled} onChange={(v) => void set({ sparkleEnabled: v })} /></Row>
      <Row label="Bump Scale"><CompactSlider value={c.sparkleBumpScale} {...SPARKLE_SLIDERS.sparkleBumpScale} onChange={(v) => void set({ sparkleBumpScale: v })} /></Row>
      <Row label="Flake Scale"><CompactSlider value={c.sparkleFlakeScale} {...SPARKLE_SLIDERS.sparkleFlakeScale} onChange={(v) => void set({ sparkleFlakeScale: v })} /></Row>
      <Row label="Flake Radius"><CompactSlider value={c.sparkleFlakeRadius} {...SPARKLE_SLIDERS.sparkleFlakeRadius} onChange={(v) => void set({ sparkleFlakeRadius: v })} /></Row>
      <Row label="Colored Flakes"><CompactCheck checked={c.sparkleColored} onChange={(v) => void set({ sparkleColored: v })} /></Row>
      {head("Custom Bump")}
      <Row label="Enabled"><CompactCheck checked={c.customBumpEnabled} onChange={(v) => void set({ customBumpEnabled: v })} /></Row>
      <Row label="Bump Scale"><CompactSlider value={c.customBumpScale} min={-1} max={1} step={0.01} onChange={(v) => void set({ customBumpScale: v })} /></Row>
      {head("Clearcoat Tint")}
      <Row label="Enabled"><CompactCheck checked={c.clearcoatTintEnabled} onChange={(v) => void set({ clearcoatTintEnabled: v })} /></Row>
      <Row label="Tint"><CompactColor value={c.clearcoatTintColor} onChange={(v) => void set({ clearcoatTintColor: v })} /></Row>
      <Row label="Thickness"><CompactSlider value={c.clearcoatTintThickness} min={0} max={5} step={0.01} onChange={(v) => void set({ clearcoatTintThickness: v })} /></Row>
      <Row label="IOR" inert={CC_TINT_IOR_INERT}><CompactSlider value={c.clearcoatTintIOR} min={0.8} max={2.5} step={0.01} onChange={(v) => void set({ clearcoatTintIOR: v })} /></Row>
      {head("SSBevel (per-material)")}
      <Row label="Enabled" inert={SS_BEVEL_INERT}><CompactCheck checked={c.ssBevelEnabled} onChange={(v) => void set({ ssBevelEnabled: v })} /></Row>
      <Row label="Radius" inert={SS_BEVEL_INERT}><CompactSlider value={c.ssBevelRadius} min={0} max={16} step={0.1} onChange={(v) => void set({ ssBevelRadius: v })} /></Row>
      {error ? <span className="px-3 pb-1.5 pt-1 text-[11px] text-ed-red">{error}</span> : null}
    </div>
  );
}
